/**
 * The editor's part of collaboration phase 2 (MonacoFileEditor): for one open file of a shared project,
 *
 * - follow: while you follow someone (presenceClient.followPerson) the editor scrolls with their cursor in this file;
 *   a key you press in the editor ends following;
 * - blame (`blameOn`, `toggleBlame`): who wrote each line, a rule in the author's colour on the left of each line, the
 *   name at the start of each block, and a hover with time, "typed" or the AI agent and model (vfs-service
 *   `GET /collab/blame`);
 * - comments: the file's threads (`threads`, `reloadComments`), a marker in the glyph margin of each commented line;
 *   a click on a marker opens the comments panel on that thread (`commentsOpen`, `activeThreadId`).
 *
 * `path` is the file's vfs path (`services/web/src/App.jsx`); nothing happens without it.
 *
 * - notes (phase 4): notes between AI agents and people about a line of this file (`notes`), a marker in the glyph
 *   margin with who and what on hover (vfs-service `GET /collab/notes?path=`);
 * - agents (phase 4): the lines an AI agent is writing in this file right now (presence `agent.lines`), a dashed band
 *   in the agent's colour with "Flow chat · Luca" after its first line; gone when the agent goes idle.
 *
 * `touch` (phones): there is no hover, so a tap on a line number with blame on picks that line (`blameLine`, the editor
 * opens its blame sheet), and a tap on the line number of a commented line opens its thread, as the marker does.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { getBlame, getComments, getNotes } from '../services/CollabService';
import { followPerson } from '../utils/presenceClient';
import { agentBandLabel, agentBandsIn, followedPerson, followTarget } from '../utils/presenceModel';
import { blameBlocks, blameHover } from '../utils/blameModel';

const REFRESH_MS = 30000;

// Blame labels as CSS: one generated class per distinct label, its text in ::after (kept in one <style> element).
const blameLabels = new Map();
function blameLabelClass(label) {
  if (blameLabels.has(label)) { return blameLabels.get(label); }
  const name = `collab-blame-l${blameLabels.size}`;
  blameLabels.set(label, name);
  if (typeof document !== 'undefined') {
    let style = document.getElementById('collab-blame-labels');
    if (!style) {
      style = document.createElement('style');
      style.id = 'collab-blame-labels';
      document.head.appendChild(style);
    }
    style.appendChild(document.createTextNode(`.${name}::after { content: ${JSON.stringify(`  ${label}`)}; }\n`));
  }
  return name;
}

export default function useEditorCollab({ editor, monaco, projectId, path, presence, touch = false }) {
  const [blameOn, setBlameOn] = useState(false);
  const [blame, setBlame] = useState(null);
  const [threads, setThreads] = useState([]);
  const [commentsOpen, setCommentsOpen] = useState(false);
  const [activeThreadId, setActiveThreadId] = useState(null);
  const [blameLine, setBlameLine] = useState(null);
  const blameOnRef = useRef(false);
  blameOnRef.current = blameOn;
  const touchRef = useRef(touch);
  touchRef.current = touch;
  const blameDecorations = useRef(null);
  const commentDecorations = useRef(null);
  const agentDecorations = useRef(null);
  const noteDecorations = useRef(null);
  const [notes, setNotes] = useState([]);
  const threadsRef = useRef([]);
  threadsRef.current = threads;
  const enabled = Boolean(editor && monaco && projectId && path);

  // Follow: scroll with the followed person's cursor in this file.
  const followed = followedPerson(presence);
  const target = followTarget(followed);
  const followLine = target && target.path === path && target.cursor ? target.cursor.line : null;
  useEffect(() => {
    if (!enabled || !followLine) { return; }
    try { editor.revealLineInCenterIfOutsideViewport(followLine); } catch { /* model gone */ }
  }, [enabled, editor, followLine]);

  // Typing stops following.
  const followingRef = useRef(null);
  followingRef.current = presence?.following || null;
  useEffect(() => {
    if (!enabled) { return undefined; }
    const sub = editor.onKeyDown(() => {
      if (followingRef.current) { followPerson(projectId, null); }
    });
    return () => sub.dispose();
  }, [enabled, editor, projectId]);

  // Blame.
  const loadBlame = useCallback(async () => {
    if (!projectId || !path) { return; }
    try {
      setBlame(await getBlame(projectId, path));
    } catch {
      setBlame(null);
    }
  }, [projectId, path]);

  useEffect(() => {
    setBlame(null);
    if (!blameOn || !projectId || !path) { return undefined; }
    loadBlame();
    const timer = setInterval(loadBlame, REFRESH_MS);
    return () => clearInterval(timer);
  }, [blameOn, projectId, path, loadBlame]);

  useEffect(() => {
    if (!enabled) { return undefined; }
    if (!blameDecorations.current) { blameDecorations.current = editor.createDecorationsCollection([]); }
    const you = presence?.you || null;
    const lineCount = editor.getModel()?.getLineCount() || 0;
    const decorations = blameOn && blame ? blameBlocks(blame.lines).flatMap((block) => {
      const out = [];
      for (let line = block.start; line <= Math.min(block.end, lineCount); line += 1) {
        out.push({
          range: new monaco.Range(line, 1, line, 1),
          options: {
            isWholeLine: true,
            linesDecorationsClassName: `collab-blame-line collab-blame-s${block.entry.agent ? `a${block.entry.via === 'Flow' ? 3 : 4}` : (block.entry.author?.slot ?? 0)}`,
            hoverMessage: line === block.start ? { value: blameHover(block.entry, you) } : undefined,
          },
        });
      }
      // The label after the block's first line: a generated class whose ::after holds it (Monaco's injected text is
      // not drawn in this editor).
      if (block.start <= lineCount) {
        out.push({
          range: new monaco.Range(block.start, 1, block.start, 1),
          options: { isWholeLine: true, afterContentClassName: `collab-blame-after ${blameLabelClass(block.label(you))}` },
        });
      }
      return out;
    }) : [];
    try { blameDecorations.current.set(decorations); } catch { /* disposed */ }
    return undefined;
  }, [enabled, editor, monaco, blame, blameOn, presence?.you]);

  // Agents writing here: one dashed band per range they touched, labelled on the first line of their first range.
  const bandsKey = JSON.stringify(agentBandsIn(presence, path).map(({ agent, ranges }) => [agent.id, agent.slot, agentBandLabel(agent, presence?.you), ranges]));
  useEffect(() => {
    if (!enabled) { return undefined; }
    if (!agentDecorations.current) { agentDecorations.current = editor.createDecorationsCollection([]); }
    const lineCount = editor.getModel()?.getLineCount() || 0;
    const decorations = [];
    JSON.parse(bandsKey).forEach(([, slot, label, ranges]) => {
      const ink = `collab-agent-band-s${slot === 4 ? 4 : 3}`;
      ranges.forEach(([from, to], index) => {
        const last = Math.min(to, lineCount);
        for (let line = from; line <= last; line += 1) {
          decorations.push({
            range: new monaco.Range(line, 1, line, 1),
            options: {
              isWholeLine: true,
              className: `collab-agent-band ${ink}${line === from ? ' is-first' : ''}${line === last ? ' is-last' : ''}`,
              hoverMessage: line === from ? { value: `${label} is writing here` } : undefined,
            },
          });
        }
        if (index === 0 && from <= lineCount) {
          decorations.push({
            range: new monaco.Range(from, 1, from, 1),
            options: { isWholeLine: true, afterContentClassName: `collab-agent-band-label ${ink} ${blameLabelClass(label)}` },
          });
        }
      });
    });
    try { agentDecorations.current.set(decorations); } catch { /* disposed */ }
    return undefined;
  }, [enabled, editor, monaco, bandsKey]);

  // Notes about lines of this file.
  const reloadNotes = useCallback(async () => {
    if (!projectId || !path) { setNotes([]); return; }
    try {
      const answer = await getNotes(projectId, { path });
      setNotes(answer?.notes || []);
    } catch {
      setNotes([]);
    }
  }, [projectId, path]);

  useEffect(() => {
    setNotes([]);
    if (!projectId || !path) { return undefined; }
    reloadNotes();
    const timer = setInterval(reloadNotes, REFRESH_MS);
    const onNote = (event) => { if (event?.detail?.projectId === projectId) { reloadNotes(); } };
    window.addEventListener('collab:note', onNote);
    return () => {
      clearInterval(timer);
      window.removeEventListener('collab:note', onNote);
    };
  }, [projectId, path, reloadNotes]);

  useEffect(() => {
    if (!enabled) { return undefined; }
    if (!noteDecorations.current) { noteDecorations.current = editor.createDecorationsCollection([]); }
    const lineCount = editor.getModel()?.getLineCount() || 0;
    const byLine = new Map();
    notes.forEach((note) => {
      if (Number.isInteger(note.line) && note.line >= 1 && note.line <= lineCount) {
        byLine.set(note.line, [...(byLine.get(note.line) || []), note]);
      }
    });
    const decorations = [...byLine.entries()].map(([line, list]) => ({
      range: new monaco.Range(line, 1, line, 1),
      options: {
        glyphMarginClassName: `collab-note-glyph${list.some((n) => n.by?.kind === 'agent') ? ' is-agent' : ''}`,
        glyphMarginHoverMessage: { value: list.map((n) => `**${n.from}** for ${n.to === 'agents' ? 'the AI agents' : n.to === 'people' ? 'everyone' : n.to}: ${n.text}`).join('\n\n') },
      },
    }));
    try { noteDecorations.current.set(decorations); } catch { /* disposed */ }
    return undefined;
  }, [enabled, editor, monaco, notes]);

  // Comments.
  const reloadComments = useCallback(async () => {
    if (!projectId || !path) { setThreads([]); return; }
    try {
      const answer = await getComments(projectId, { path });
      setThreads(answer?.threads || []);
    } catch {
      setThreads([]);
    }
  }, [projectId, path]);

  useEffect(() => {
    setThreads([]);
    setActiveThreadId(null);
    if (!projectId || !path) { return undefined; }
    reloadComments();
    const timer = setInterval(reloadComments, REFRESH_MS);
    return () => clearInterval(timer);
  }, [projectId, path, reloadComments]);

  useEffect(() => {
    if (!enabled) { return undefined; }
    if (!commentDecorations.current) { commentDecorations.current = editor.createDecorationsCollection([]); }
    const lineCount = editor.getModel()?.getLineCount() || 0;
    const byLine = new Map();
    threads.forEach((thread) => {
      if (Number.isInteger(thread.line) && thread.line >= 1 && thread.line <= lineCount) {
        const open = byLine.get(thread.line);
        byLine.set(thread.line, { resolved: (open ? open.resolved : true) && thread.resolved, count: (open?.count || 0) + 1 + (thread.replies || []).length });
      }
    });
    const decorations = [...byLine.entries()].map(([line, info]) => ({
      range: new monaco.Range(line, 1, line, 1),
      options: {
        glyphMarginClassName: `collab-comment-glyph${info.resolved ? ' is-resolved' : ''}`,
        glyphMarginHoverMessage: { value: `${info.count} comment${info.count === 1 ? '' : 's'}${info.resolved ? ' (resolved)' : ''} · click to open` },
      },
    }));
    try { commentDecorations.current.set(decorations); } catch { /* disposed */ }
    return undefined;
  }, [enabled, editor, monaco, threads]);

  // A click on a comment marker opens the panel on that line's thread.
  useEffect(() => {
    if (!enabled) { return undefined; }
    const sub = editor.onMouseDown((event) => {
      const types = monaco.editor.MouseTargetType || {};
      const type = event?.target?.type;
      const line = event?.target?.position?.lineNumber;
      const onGutter = type === types.GUTTER_LINE_NUMBERS || type === types.GUTTER_LINE_DECORATIONS;
      if (touchRef.current && onGutter && blameOnRef.current && line) {
        setBlameLine({ line, at: Date.now() });
        return;
      }
      if (type !== types.GUTTER_GLYPH_MARGIN && !(touchRef.current && onGutter)) { return; }
      const thread = threadsRef.current.find((t) => t.line === line);
      if (thread) {
        setActiveThreadId(thread.id);
        setCommentsOpen(true);
      }
    });
    return () => sub.dispose();
  }, [enabled, editor, monaco]);

  // Links from the Activity timeline (components/collab/ActivityPanel.jsx): blame this file, or open a comment thread.
  useEffect(() => {
    if (!projectId || !path) { return undefined; }
    const mine = (event) => event?.detail && (!event.detail.projectId || event.detail.projectId === projectId) && event.detail.path === path;
    const onBlame = (event) => { if (mine(event)) { setBlameOn(true); } };
    const onThread = (event) => {
      if (!mine(event)) { return; }
      setCommentsOpen(true);
      if (event.detail.threadId) { setActiveThreadId(event.detail.threadId); }
    };
    window.addEventListener('collab:blame', onBlame);
    window.addEventListener('collab:openThread', onThread);
    return () => {
      window.removeEventListener('collab:blame', onBlame);
      window.removeEventListener('collab:openThread', onThread);
    };
  }, [projectId, path]);

  // A new file: the collections start empty again on the next model.
  useEffect(() => () => {
    try { blameDecorations.current?.clear(); } catch { /* disposed */ }
    try { commentDecorations.current?.clear(); } catch { /* disposed */ }
    try { agentDecorations.current?.clear(); } catch { /* disposed */ }
    try { noteDecorations.current?.clear(); } catch { /* disposed */ }
  }, [path]);

  return {
    blameOn,
    setBlameOn,
    toggleBlame: () => setBlameOn((on) => !on),
    blame,
    blameLine,
    setBlameLine,
    threads,
    reloadComments,
    commentsOpen,
    setCommentsOpen,
    activeThreadId,
    setActiveThreadId,
    following: followed,
    notes,
    reloadNotes,
  };
}
